import { useCallback, useEffect, useState } from "react";
import { PLANS } from "@riseafrica/scoring";
import { CONFIG } from "../config";
import { fmt } from "../lib/format";

/**
 * Au-delà de combien de remises un rapport mérite un regard.
 *
 * Deux ou trois sont normales : le téléphone, l'ordinateur, une relecture trois
 * semaines plus tard. Douze ne le sont plus. Le seuil ne bloque rien — il
 * décide seulement quand la ligne se met à le dire.
 */
const SEUIL_REMISES = 12;

/**
 * Le back-office d'exploitation.
 *
 * **Pourquoi cet écran existe.** Le tableau de bord juste en dessous mesure :
 * des séries, des taux, des tendances. Il répond à « comment va le produit ».
 * Il ne répond à aucune des questions qu'une journée d'incident pose vraiment —
 * quelle transaction est bloquée, pourquoi celle-là, et que puis-je y faire
 * maintenant.
 *
 * Le 20/08/2026, un acheteur a attendu son rapport une journée entière pendant
 * que la seule façon de savoir pourquoi était de lire des traces d'intégration
 * continue. Un produit qui encaisse doit pouvoir être exploité par la personne
 * qui le tient.
 *
 * **Ce qu'il ne montre pas.** Aucun numéro, aucun libellé de tâche. Le serveur
 * ne les envoie pas — il rend « un contact existe », « le rapport est encore
 * rédigeable ». Un back-office est un endroit d'où l'on agit, pas un endroit où
 * l'on consulte des gens.
 *
 * **Ce qu'il place en premier.** Les gens qui attendent. Le reste vient après.
 */

export interface LigneTransaction {
  txId: string;
  plan: string;
  operateur: string;
  statut: string;
  montant: number;
  motif: string | null;
  reprises: number;
  remises?: number;
  adresseOuverte?: boolean;
  derniereRemise?: string | null;
  cree: string | null;
  maj: string | null;
  contact: boolean;
  rediGeable: boolean;
  remboursementDemandeLe: string | null;
  rembourseLe: string | null;
  essai: boolean;
  /** Achat simulé en échec, que « Classer l'essai » peut sortir du compteur. */
  classable?: boolean;
  classeLe?: string | null;
  /** Pour un essai seulement : « report », « independant » ou « entreprise ». */
  variante?: string | null;
  url: string | null;
  note: string | null;
  etape: string | null;
  etapeLe: string | null;
  demarree: string | null;
}

interface LigneEngagement {
  txId: string;
  plan: string;
  statut: string;
  debut: string | null;
  fin: string | null;
  echu: boolean;
  honoreLe: string | null;
}

interface LigneJournal {
  le: string;
  action: string;
  txId: string;
  note: string | null;
}

/** L'état de la boutique : ouverte, ou fermée et depuis quand. */
interface EtatVente {
  ouverte: boolean;
  fermeeDepuis: string | null;
  motif: string | null;
}

/** Ce que prend une rédaction, mesuré sur les rapports réellement livrés. */
interface Durees {
  mesurees: number;
  medianeMinutes: number | null;
  p90Minutes: number | null;
  pireMinutes: number | null;
  partCache: number | null;
}

/** Où en est la dépense du jour. Au plafond, la vente se ferme. */
interface Depense {
  depenseXof: number;
  plafondXof: number;
  resteXof: number | null;
  partPourCent: number | null;
  appels: number;
}

interface MetierDemande {
  metier: string;
  vus: number;
  /** Absent d'un serveur antérieur au 01/10/2026. */
  auReferentiel?: boolean;
}

/** La note des rapports réellement partis, sur cinq, et ce qui la tire vers le bas. */
interface Qualite {
  mesures: number;
  moyenne: number | null;
  moyenneDocument: number | null;
  pages: number | null;
  pire: number | null;
  familles: Record<string, number>;
  axesFaibles: Array<{ id: string; occurrences: number }>;
  // Absents d'un serveur antérieur au 01/10/2026 : l'écran s'en passe.
  profils?: Array<{
    profil: ProfilRapport;
    mesures: number;
    moyenne: number;
    axesFaibles: Array<{ id: string; partPourCent: number }>;
  }>;
  versions?: Array<{
    profil: ProfilRapport;
    consignes: string;
    finition: string;
    mesures: number;
    moyenne: number;
    retouchesParRapport: number | null;
  }>;
  retouches?: {
    rediges: number;
    etapes: Array<{ etape: string; rapportsTouches: number; partPourCent: number; chaines: number }>;
  };
  relecture?: {
    relus: number;
    defauts: Array<{ id: string; rapportsTouches: number; partPourCent: number; occurrences: number }>;
  };
}

type ProfilRapport = "salarie" | "independant" | "dirigeant";

const PROFILS: Record<ProfilRapport, string> = {
  salarie: "Salarié",
  independant: "À son compte",
  dirigeant: "Dirigeant",
};

/** Ce que chaque étape de la finition reprend. Miroir de `report/retouches.ts`. */
const ETAPES_FINITION: Record<string, string> = {
  genre: "accords de genre et tournures réécrites",
  statut: "« votre poste » dit à qui travaille à son compte",
  destinataires: "« à confier à » réécrit en rôle",
  confidentialite: "filet de confidentialité ajouté",
  plafond: "gain ramené au barème",
  chiffres: "phrase chiffrée retirée de la synthèse",
  defauts: "défaut grave trouvé avant livraison",
  reprise: "rédaction reprise pour un défaut grave",
};

/** Ce que la relecture du PDF cherche. Miroir de `report/relecture.ts`. */
const DEFAUTS_RELECTURE: Record<string, string> = {
  coupe_imprimee: "phrase coupée en « … »",
  guillemet_orphelin: "guillemet seul en bout ou en tête de ligne",
  majuscule_en_suite: "majuscule au milieu d'une ligne",
  espace_en_barre: "espace imprimée en « / » dans un nombre",
  accord_genre: "accord de genre adressé au lecteur",
  heures_inconnues: "heures qu'aucune déclaration ne porte",
  indice_incoherent: "valeur sur cent que le code n'a pas calculée",
  chiffre_prete: "chiffre du barème prêté à la personne",
  tic_connu: "faute ou tournure déjà corrigée une fois",
};

/** Une rédaction d'essai, telle que `functions/src/essaiRedaction.ts` la mesure. */
interface MesureEssai {
  cas: "salarie" | "independant" | "dirigeant";
  redige: boolean;
  motif: string | null;
  dureeS: number;
  tientDansLeBudget: boolean;
  tentatives: number;
  tokensIn: number;
  tokensOut: number;
  coutXof: number;
  note: number | null;
  faibles: string[];
  retouches: Record<string, number> | null;
}

/** Miroir de `EssaiMesure` dans `functions/src/essaiMesure.ts`. */
interface EssaiMesure {
  id: string;
  modele: string;
  effort: string;
  fini: boolean;
  arret: string | null;
  demandeLe: string;
  finiLe: string | null;
  cas: Array<"salarie" | "independant" | "dirigeant">;
  mesures: MesureEssai[];
}

/** Un voyant de la tour de contrôle. Miroir exact de `etatDeVol.ts`. */
interface Condition {
  cle: string;
  titre: string;
  feu: "vert" | "orange" | "rouge" | "inconnu";
  dit: string;
  geste?: string;
  bloquant: boolean;
}

interface Etat {
  /** Peut-on vendre, maintenant ? Neuf conditions et un verdict. */
  etatDeVol?: { peutVendre: boolean | null; conditions: Condition[] };
  vente?: EtatVente;
  durees?: Durees;
  motifsSemaine?: Record<string, number>;
  depense?: Depense;
  metiers?: MetierDemande[];
  qualite?: Qualite;
  specimen?: {
    publie: boolean;
    publieLe: string | null;
    /*
      L'axe entreprise a son propre spécimen depuis le 25/08. Il n'y en avait
      qu'un, écrit pour un salarié, et le rapport à 15 000 F se vendait sans
      qu'un acheteur puisse en voir une page.
    */
    entreprise?: { publie: boolean; publieLe: string | null };
  };
  outils?: {
    lignes: Array<{ categorie: string; outils: string; note: string }>;
    majLe: string | null;
    /** La liste servie est celle du code, pas encore la vôtre. */
    parDefaut?: boolean;
  };
  /** Le canal courriel a-t-il sa clé **et** son expéditeur ? */
  courrielArme?: boolean;
  /** Les derniers essais mesurés d'un réglage de rédaction — 02/10/2026. */
  essaisMesures?: EssaiMesure[];
  modelesEssai?: string[];
  reglageProduction?: { modele: string; effort: string };
  /*
    Les réglages portent aussi du texte depuis le 25/08 — l'adresse de contact,
    le canal des 90 jours — et les dix mentions légales depuis le 31/08. Le type
    ne le disait pas : les branches `typeof … === "string"` qui les rendent
    étaient donc du code mort aux yeux de TypeScript, et la seule façon de lire
    une valeur textuelle était de la transtyper. Un transtypage qui répare un
    type faux le laisse faux pour le suivant.
  */
  reglages?: Record<string, boolean | number | string>;
  reglagesPoses?: Record<string, boolean | number | string>;
  sentinelle?: { le: string | null; voie: "courriel" | "journal" | null; soucis: string[] };
  tunnels?: { abandonnes: number; aboutis: number; heuresSansPaiement: number | null };
  /*
    La pression du limiteur de débit — 31/08/2026.

    Aucune adresse n'y figure, et ce n'est pas un oubli : la collection lue est
    indexée par adresse IP, qui est une donnée personnelle. Trois nombres par
    portée répondent à toutes les questions d'exploitation sans qu'aucune
    demande de savoir *qui*.
  */
  pression?: {
    fenetre: string[];
    tronquee: boolean;
    lignes: Array<{
      portee: string;
      plafond: number;
      compteurs: number;
      maxi: number;
      bloquees: number;
    }>;
  };
  rapportsDus?: number;
  rapportsDusMotifs?: Record<string, number>;
  essaisSimulesBloques?: number;
  testsEnEchec?: LigneTransaction[];
  accompagnements?: { ouverts: number; echus: number };
  transactions: LigneTransaction[];
  engagements: LigneEngagement[];
  journal: LigneJournal[];
  indisponible?: boolean;
}

/** Ce que chaque statut veut dire, en une ligne, pour qui exploite. */
const STATUTS: Record<string, string> = {
  en_attente: "en attente de l'opérateur",
  paye: "payé — rédaction en file",
  livre: "livré",
  echec_livraison: "payé, non délivré",
  rembourse: "remboursé",
  echoue: "paiement échoué",
  annule: "annulé",
};

/** Ce que chaque motif veut dire, et ce qu'il implique. */
/**
 * Ce que chaque portée du limiteur protège, en français.
 *
 * Le serveur envoie le code (`rl_simuler_code`) parce que c'est la clé sous
 * laquelle il compte ; l'écran doit dire ce que ce compteur garde, sinon la
 * table demande d'aller lire le code pour être lue.
 */
const PORTEES: Record<string, string> = {
  rl: "Diagnostic salarié",
  rl_action: "Plan d'action salarié",
  rl_entreprise: "Diagnostic dirigeant",
  rl_entreprise_plan: "Plan d'action dirigeant",
  rl_results: "Enregistrement d'un résultat",
  rl_pilotage: "Connexion à ce tableau de bord",
  rl_pilotage_global: "Connexion à ce tableau de bord, toutes adresses",
  rl_pay: "Ouverture d'un paiement",
  rl_relance: "Récupération d'un rapport payé",
  rl_remboursement: "Demande de remboursement",
  rl_effacement: "Demande d'effacement",
  rl_events: "Collecte d'évènements",
  rl_simuler: "Opérateur simulé",
  rl_simuler_code: "Codes faux à l'opérateur simulé",
  rl_attestation: "Émission d'attestation",
  rl_courriel_test: "Courriel d'essai",
  rl_progression: "Page de progression",
};

const MOTIFS: Record<string, string> = {
  profil_absent: "les données du diagnostic ne sont plus rattachées — aucune reprise n'aboutira",
  validation_trajectoire: "la rédaction n'a pas respecté le format attendu",
  json: "la réponse du modèle n'était pas lisible",
  budget: "le plafond de dépense du jour était atteint",
  // Le seul motif qui se répare en deux minutes, et hors du code : recharger le
  // compte du fournisseur. Il se rangeait dans « autre », donc on cherchait une
  // cause technique là où il n'y avait qu'une facture à payer.
  credit_epuise: "le compte du fournisseur de modèle est à sec — rechargez-le, rien d'autre à faire",
  fournisseur_sature: "le fournisseur était débordé — une relance a des chances d'aboutir",
  delai: "le modèle a dépassé le délai maximal",
  tronque: "la réponse a été coupée au plafond de jetons",
  non_renseigne: "échec antérieur à l'inscription des motifs",
};

/**
 * Ce que le porteur peut ouvrir ou fermer, et ce que cela coûte de le fermer.
 *
 * Le libellé dit l'effet, pas le nom technique : un bouton d'exploitation qui
 * demande de connaître le code n'est pas un bouton, c'est un aide-mémoire.
 */
/**
 * Les interrupteurs, dans l'ordre où ils comptent.
 *
 * La vente d'abord, parce qu'elle est le seul dont la fermeture évite de
 * prendre l'argent de quelqu'un. Les canaux ensuite. Le plafond en dernier :
 * c'est le seul qui porte un nombre, et le seul que l'exploitation ne peut que
 * **resserrer** — le déploiement fixe la borne haute.
 */
const REGLAGES: ReadonlyArray<{
  cle: string;
  titre: string;
  explication: string;
  nombre?: { unite: string; zero: string };
  /**
   * Un réglage qui porte du texte — 25/08/2026.
   *
   * `choix` en fait une liste fermée plutôt qu'un champ libre : le canal des
   * quatre-vingt-dix jours n'a que quatre valeurs, et laisser saisir la
   * cinquième reviendrait à laisser poser un réglage que le serveur refusera
   * sans que l'écran l'ait dit.
   */
  texte?: { exemple: string; choix?: Array<{ valeur: string; libelle: string }> };
}> = [
  {
    cle: "venteOuverte",
    titre: "la vente",
    explication:
      "Fermée, /pay/init refuse tout encaissement — le diagnostic gratuit continue, il a son mode dégradé. À fermer dès que vous savez que la rédaction n'aboutira pas : compte du fournisseur à sec, panne connue. La fermeture automatique existe déjà, mais elle attend qu'une livraison ait échoué ; celle-ci n'attend pas.",
  },
  {
    cle: "accompagnementActif",
    titre: "la formule à 9 000 F",
    explication:
      "Elle promet la mise en place des trois premières actions du rapport, par écrit, puis la seconde mesure à quatre-vingt-dix jours commentée. Elle livrait exactement le rapport à 3 000 F : à n'ouvrir que le jour où quelqu'un tient réellement ces échanges. Tant que rien n'est posé ici, c'est la variable de déploiement qui décide.",
  },
  {
    cle: "progressionActive",
    titre: "le rapport de progression à 90 jours",
    explication:
      "Allumé, chaque rapport livré garde une ligne de base anonyme — tâches du métier, heures, actions, sans nom ni adresse — et imprime le lien de sa page de progression. À quatre-vingt-dix jours, la personne y redéclare ses heures, voit gratuitement l'écart de son indice, et peut acheter le rapport de progression : le même rapport à 3 000 F, avec en plus « Ce qui a bougé », calculé par le code. Rien ne demande votre temps. À n'allumer qu'après les premières ventes réelles.",
  },
  {
    cle: "accesRestreint",
    titre: "le rodage fermé",
    explication:
      "Ouvert, l'encaissement réel exige le code d'accès. Le fermer ouvre la vente au public — à ne faire qu'une fois les mentions légales réelles en place. Sans code posé côté déploiement, la vente est publique quoi qu'on mette ici.",
  },
  {
    cle: "metaActif",
    titre: "la mesure publicitaire Meta",
    explication:
      "Allumée, le site propose le bandeau de consentement, charge le pixel Meta pour qui l'accepte, et signale à Meta chaque achat confirmé par l'opérateur — avec l'identifiant de clic de l'annonce et, haché, l'adresse ou le numéro de l'acheteur, jamais son métier ni son résultat. Rien ne part sans identifiant de jeu de données configuré ni sans consentement. L'éteindre coupe tout, immédiatement, sans déploiement.",
  },
  {
    cle: "dirigeantActif",
    titre: "l'axe dirigeant",
    explication:
      "Fermé, le site retire ses portes d'entrée — menu, accueil, pied de page, écran de résultat —, /dirigeant annonce sa fermeture et renvoie vers le diagnostic individuel, et le serveur refuse le questionnaire, le plan et l'encaissement du rapport d'entreprise. Les rapports déjà payés restent accessibles. Le rouvrir ne demande aucun déploiement. Pensez à couper aussi la campagne Meta « Dirigeants ».",
  },
  {
    cle: "modeCv",
    titre: "le CV collé",
    explication:
      "Le second mode du volet 1. Fermé, l'onglet disparaît, la question de la FAQ qui en parle aussi, et le serveur refuse toute analyse de CV. Il coûte plus cher que le mode métier : un CV n'est jamais mis en cache.",
  },
  {
    cle: "courrielRemise",
    titre: "l'envoi du rapport par courriel",
    explication:
      "Ne remplace pas l'armement du canal : sans clé ni expéditeur attachés à la livraison, rien ne part de toute façon. Sert à arrêter d'envoyer sans désarmer, le jour où le fournisseur rejette ou marque en indésirable.",
  },
  {
    cle: "whatsappRemise",
    titre: "l'envoi du rapport par WhatsApp",
    explication:
      "Même principe : couper l'envoi sans retirer le jeton. Les rappels à trois et quatre-vingt-dix jours partent du même canal et suivent le même interrupteur.",
  },
  {
    cle: "prixEntreprise",
    titre: "le prix du rapport dirigeant",
    explication:
      "Ce champ ne fixe pas le prix : il déclare celui que Chariow facture. Leur checkout n'a pas de champ montant — le prix vit sur le produit, dans leur tableau de bord. Notre côté confronte les deux et refuse d'envoyer l'acheteur s'ils divergent. PROCÉDURE : fermez la vente ci-dessus, changez le prix chez Chariow, changez-le ici, rouvrez. Entre les deux, aucun paiement dirigeant n'aboutit.",
    nombre: { unite: "F CFA", zero: "prix du code" },
  },
  {
    cle: "contactCourriel",
    titre: "l'adresse à laquelle on vous écrit",
    explication:
      "Elle est écrite sous la garantie de remboursement, sous l'exercice des droits sur les données, et au pied du site. C'est la seule voie de recours du produit : une adresse que personne ne relève est pire qu'une absence d'adresse, parce qu'elle fait croire à quelqu'un qu'il a écrit. Elle vivait en dur dans le paquet du navigateur — la changer demandait une livraison de code.",
    texte: { exemple: "hello@riseafrica.io" },
  },
  {
    cle: "canal90Jours",
    titre: "par où part le rendez-vous des 90 jours",
    explication:
      "Le rappel partait par WhatsApp et par lui seul : il exigeait un numéro — facultatif partout — un consentement, et un canal armé. Un acheteur sans numéro n'avait donc rien à quatre-vingt-dix jours, alors que ce rendez-vous est ce qui transforme un achat unique en mesure répétée. « Aucun » n'est pas une faute : mieux vaut ne rien promettre que promettre par un canal éteint.",
    texte: {
      exemple: "whatsapp",
      choix: [
        { valeur: "whatsapp", libelle: "WhatsApp seul" },
        { valeur: "courriel", libelle: "Courriel seul" },
        { valeur: "les_deux", libelle: "Les deux" },
        { valeur: "aucun", libelle: "Aucun — ne rien envoyer" },
      ],
    },
  },
  {
    cle: "plafondJourFcfa",
    titre: "le plafond de dépense du jour",
    explication:
      "En francs CFA. Au-delà, le diagnostic bascule en mode dégradé signalé et la vente se ferme — le service ne s'arrête pas. Vous pouvez le baisser, ou en poser un là où le déploiement n'en pose aucun. Vous ne pouvez pas le relever au-dessus de celui du déploiement : sinon un accès à cet écran suffirait à faire dépenser sans limite.",
    nombre: { unite: "F CFA par jour", zero: "aucun plafond" },
  },
];

/**
 * Les dix mentions légales, saisissables ici — 31/08/2026.
 *
 * Elles vivaient à `null` dans le paquet construit, et les remplir demandait
 * une livraison de code. Ce ne sont pourtant pas des décisions d'ingénierie :
 * ce sont des faits inscrits sur des papiers — statuts, extrait RCCM,
 * attestation fiscale, récépissé ARTCI — que le porteur est seul à pouvoir
 * lire. Mettre un développeur sur le chemin critique du lancement pour recopier
 * dix chaînes n'avait aucune raison d'être.
 *
 * Elles ont leur bloc, et ne sont pas mêlées aux interrupteurs : un interrupteur
 * se bascule au gré de l'exploitation, une mention se pose une fois et ne bouge
 * plus. Chaque entrée dit **où trouver la valeur**, parce que c'est là qu'est la
 * difficulté réelle — pas dans la saisie.
 */
const MENTIONS_PILOTAGE: ReadonlyArray<{
  cle: string;
  titre: string;
  ou: string;
  exemple: string;
  /** Verrouille l'ouverture de la vente au public — identité et adresse, loi n° 2013-546. */
  requise: boolean;
}> = [
  {
    cle: "mentionFormeJuridique",
    titre: "Forme juridique",
    ou: "vos statuts",
    exemple: "SARL",
    requise: true,
  },
  {
    cle: "mentionSiege",
    titre: "Adresse du siège",
    ou: "statuts ou extrait RCCM — sans la ville ni le pays, ils sont ailleurs sur la page",
    exemple: "Rue du Commerce, immeuble…",
    requise: true,
  },
  {
    cle: "mentionRccm",
    titre: "Numéro RCCM — facultatif, si l'éditeur est immatriculé",
    ou: "extrait du registre du commerce et du crédit mobilier ; la loi le demande « le cas échéant »",
    exemple: "CI-ABJ-2026-B-12345",
    requise: false,
  },
  {
    cle: "mentionNcc",
    titre: "Numéro de compte contribuable — facultatif",
    ou: "attestation de la Direction générale des impôts ; absent, la page ne le nomme pas",
    exemple: "1234567 A",
    requise: false,
  },
  {
    cle: "mentionResponsablePublication",
    titre: "Responsable de la publication — facultatif",
    ou: "votre décision — la personne physique qui répond du contenu publié ; absent, la page ne le nomme pas",
    exemple: "Prénom Nom",
    requise: false,
  },
  {
    cle: "mentionReferenceArtci",
    titre: "Référence ARTCI — facultatif",
    ou: "récépissé de déclaration, numéro et date ; absent, la page ne le nomme pas",
    exemple: "N° 0001234 du 12/09/2026",
    requise: false,
  },
  {
    cle: "mentionDelaiRemboursement",
    /*
      La seule mention dont le contenu est tenu — 02/09/2026, et dérivée
      depuis le 08/09 : absente, le serveur publie « 14 jours ». Cette case
      reste pour qui veut une formulation plus précise ; le serveur refuse
      toujours un autre nombre de jours, et l'exemple le montre.
    */
    titre: "Délai de remboursement — facultatif, « 14 jours » sinon",
    ou: "quatorze jours — le délai déjà promis à l'acheteur ; seule la formulation vous appartient",
    exemple: "Remboursement sans justification dans les quatorze jours.",
    requise: false,
  },
];

/*
  Les mentions dérivées du déploiement — 08/09/2026.

  Région d'hébergement, fournisseur du modèle, juridiction : le serveur les
  sait et les publie lui-même. Elles ont quitté cette console, où un humain les
  recopiait depuis une console Google Cloud et depuis le code — deux copies qui
  ne pouvaient que dériver. Le délai de remboursement est dérivé lui aussi,
  mais sa case reste, parce que sa formulation appartient au porteur.
*/

function date(valeur: string | null): string {
  if (!valeur) return "—";
  const d = new Date(valeur);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** Les cinq familles du barème, en clair. */
const FAMILLES: Record<string, string> = {
  // Barème du contenu — ce qui est écrit.
  personnalisation: "Le rapport parle-t-il de cette personne",
  chiffres: "Ce qui est avancé est-il mesurable",
  executabilite: "Peut-on faire ce qui est écrit",
  honnetete: "Le rapport tient-il ce qu'il a le droit de dire",
  forme: "Le texte est-il tenu",
  // Barème du document — ce qui se voit une fois composé.
  structure: "Le document est-il celui qu'on a annoncé",
  presentation: "La mise en page est-elle tenue",
  coherence: "Le document dit-il ce que le contenu contient",
};

/** Ce que chaque étape de livraison veut dire, pour qui regarde. */
const ETAPES: Record<string, string> = {
  redaction: "rédaction par le modèle",
  pdf: "mise en page du document",
  rangement: "rangement et envoi",
};

/** Depuis combien de temps, en clair. Sous la minute, on dit « à l'instant ». */
function depuis(valeur: string | null): string {
  if (!valeur) return "";
  const debut = new Date(valeur).getTime();
  if (Number.isNaN(debut)) return "";
  const minutes = Math.floor((Date.now() - debut) / 60_000);
  if (minutes < 1) return "à l'instant";
  return `depuis ${minutes} min`;
}

/**
 * L'état d'une livraison en cours, en une ligne.
 *
 * Aucune barre, aucun pourcentage : ils seraient inventés, et la règle n° 6 les
 * interdit. Ce qui s'affiche est ce qui s'est produit — l'étape écrite par la
 * livraison elle-même, et depuis quand elle dure. C'est ce qui permet de faire
 * la différence entre « ça avance » et « ça ne bouge plus », qui est la seule
 * question qu'on se pose devant un rapport qui tarde.
 */
function progression(t: LigneTransaction): string | null {
  if (t.statut !== "paye") return null;
  if (t.etape) return `${ETAPES[t.etape] ?? t.etape} — ${depuis(t.etapeLe)}`;
  if (t.demarree) return `prise en charge ${depuis(t.demarree)}`;
  return "en file — la rédaction démarre dans la minute";
}

/** Les huit premiers caractères suffisent à reconnaître une transaction. */
function court(txId: string): string {
  return txId.slice(0, 8);
}

/**
 * Qui attend vraiment, et ce qui n'est qu'un test — 29/09/2026.
 *
 * Seuls les débits réels entrent dans « acheteur(s) ont payé et n'ont pas
 * reçu leur rapport » : ni un essai, ni un achat simulé, classé ou non. Les
 * achats de test en échec viennent de la lecture dédiée du serveur, la liste
 * des achats ne gardant que les récents.
 */
export function trierLesEchecs(
  transactions: ReadonlyArray<LigneTransaction>,
  testsLusAPart: ReadonlyArray<LigneTransaction> | undefined,
): { attendent: LigneTransaction[]; testsEnEchec: LigneTransaction[] } {
  return {
    attendent: transactions.filter(
      (t) => t.statut === "echec_livraison" && !t.essai && !t.classable && !t.classeLe,
    ),
    testsEnEchec: [...(testsLusAPart ?? transactions.filter((t) => t.classable === true))],
  };
}

export function Exploitation({ session }: { session: string }) {
  const [etat, setEtat] = useState<Etat | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [tout, setTout] = useState(false);

  const charger = useCallback(async () => {
    try {
      // La session voyage dans `x-pilotage`, jamais dans l'adresse : voir la
      // note de `session()` dans `functions/src/backoffice.ts`.
      const r = await fetch(`${CONFIG.pilotageEndpoint}/exploitation`, {
        headers: { "x-pilotage": session },
      });
      if (!r.ok) {
        setErreur("L'exploitation n'a pas pu être lue.");
        return;
      }
      setErreur(null);
      setEtat((await r.json()) as Etat);
    } catch {
      setErreur("L'exploitation n'a pas pu être lue.");
    }
  }, [session]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Y a-t-il une livraison en cours ? C'est ce qui décide du suivi. */
  const enVol =
    (etat?.transactions.some((t) => t.statut === "paye") ?? false) ||
    // Un essai mesuré rédige en arrière-plan : on le suit comme une livraison.
    (etat?.essaisMesures?.some((e) => !e.fini) ?? false);

  /*
    Relecture automatique tant qu'une livraison est en cours.

    Une rédaction prend jusqu'à neuf minutes. Demander de recharger la page à
    l'aveugle pendant ce temps-là revient à laisser croire qu'il ne se passe
    rien — et c'est exactement l'impression qui a fait presser cinq fois le
    bouton de reprise le 20/08. On relit toutes les quinze secondes, et
    **seulement** quand il y a quelque chose à suivre : une page qui interroge
    le serveur sans raison est une facture qui court.
  */
  useEffect(() => {
    if (!enVol) return;
    const minuteur = window.setInterval(() => void charger(), 15_000);
    return () => window.clearInterval(minuteur);
  }, [enVol, charger]);

  /**
   * Agir, puis relire.
   *
   * On ne devine pas le nouvel état à partir de la réponse : on redemande. Une
   * page d'exploitation qui affiche ce qu'elle croit avoir fait, plutôt que ce
   * qui est, finit par mentir au moment le plus coûteux.
   */
  const agir = async (
    action: string,
    txId: string,
    confirmation?: string,
    retirerAcces = false,
    plan?: string,
    extra?: Record<string, unknown>,
  ) => {
    if (confirmation && !window.confirm(confirmation)) return;
    setEnCours(`${action}:${txId}`);
    setErreur(null);
    try {
      const r = await fetch(`${CONFIG.pilotageEndpoint}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-pilotage": session },
        body: JSON.stringify({ action, txId, retirerAcces, plan, ...extra }),
      });
      const reponse = (await r.json()) as { erreur?: string };
      if (!r.ok) {
        setErreur(
          reponse.erreur === "profil_absent"
            ? "Les données du diagnostic ne sont plus rattachées à cet achat : aucune reprise n'aboutira. Le remboursement est dû."
            : reponse.erreur === "essai_deja_en_cours"
              ? "Un essai de ce livrable est déjà en file : son lien apparaîtra dans « Les achats » sous neuf minutes. Aucun second essai n'a été lancé."
              : `L'action n'a pas abouti (${reponse.erreur ?? r.status}).`,
        );
        return;
      }
      await charger();
    } catch {
      setErreur("L'action n'a pas abouti.");
    } finally {
      setEnCours(null);
    }
  };

  if (etat === null) return null;

  /*
    Qui a réellement payé et attend — 29/09/2026.

    Le filtre ne portait que sur le statut : un essai ou un achat simulé en
    échec s'affichait en rouge sous « acheteur(s) ont payé et n'ont pas reçu
    leur rapport », alors que personne n'avait rien payé. C'est la phrase la
    plus grave de l'écran ; elle ne compte que des débits réels.
  */
  const { attendent, testsEnEchec } = trierLesEchecs(etat.transactions, etat.testsEnEchec);
  /*
    Les essais encore en file, par livrable.

    Trois clics successifs sur « Rapport salarié » ont mis trois essais en file
    le 03/09 : le bouton ne se bloquait que le temps de la requête, et rien ne
    disait qu'un essai attendait déjà. Le serveur refuse désormais le doublon ;
    l'écran, lui, le dit avant le clic, et tient le bouton fermé.
  */
  // Salarié et indépendant partagent le plan « report » : la variante les
  // sépare, et un essai d'avant ce champ se lit par son plan.
  const essaiEnFile = (variante: string) =>
    etat.transactions.find(
      (t) => t.essai && t.statut === "paye" && (t.variante ?? t.plan) === variante,
    ) ?? null;
  const aRembourser = etat.transactions.filter(
    (t) => t.remboursementDemandeLe !== null && t.rembourseLe === null,
  );
  const echus = etat.engagements.filter((e) => e.echu && e.honoreLe === null);
  const visibles = tout ? etat.transactions : etat.transactions.slice(0, 12);

  return (
    <div aria-live="polite">
      {erreur !== null && (
        <div className="block pil-alertes">
          <p className="pil-alerte pil-rouge">{erreur}</p>
        </div>
      )}

      {/*
        La tour de contrôle, avant tout le reste.

        Elle est un **résumé**, pas un doublon : les blocs qui suivent gardent
        le détail — la date de fermeture, le geste de réouverture, l'état des
        spécimens. Ce que la tour ajoute est de lire les neuf conditions
        ensemble et d'en tirer une réponse, ce qu'aucune section ne faisait.
      */}
      {etat.etatDeVol !== undefined && <TourDeControle vol={etat.etatDeVol} />}

      {/*
        La vente fermée passe avant même les acheteurs qui attendent : tant
        qu'elle l'est, la rédaction est hors d'atteinte et rien d'autre sur
        cette page ne bougera. C'est aussi le seul écran qui puisse le dire —
        un visiteur, lui, ne voit qu'un bouton d'achat qui refuse.
      */}
      {etat.vente?.ouverte === false && (
        <div data-groupe="agir" className="block pil-alertes">
          <h2>La vente est suspendue</h2>
          <p className="pil-alerte pil-rouge">
            Depuis {date(etat.vente.fermeeDepuis)}, motif&nbsp;: {etat.vente.motif ?? "inconnu"}.
            {etat.vente.motif === "credit_epuise"
              ? " Le compte du fournisseur de modèle est à sec : rechargez-le, aucune autre action n'y changera rien."
              : ""}
          </p>
          <p className="fine">
            Aucun nouvel achat n'est encaissé — nous préférons ne rien prendre plutôt que devoir
            rembourser. Le diagnostic gratuit, lui, continue en mode dégradé. Une fois le problème
            réglé, lancez une <strong>livraison d'essai</strong> ci-dessous&nbsp;: si elle aboutit,
            la vente rouvre d'elle-même. Elle ne rouvre pas sur une déclaration, mais sur une preuve.
          </p>
        </div>
      )}

      {/*
        Les achats de test restés en échec — 29/09/2026. `/sante` les comptait
        chaque jour (`rapportsDusSimules`) sans que cet écran les montre, ni
        qu'aucun geste ne puisse les éteindre.
      */}
      {testsEnEchec.length > 0 && (
        <div data-groupe="agir" className="block">
          <h2>Achats de test en échec</h2>
          <p className="fine">
            {testsEnEchec.length} achat(s) passé(s) par l'opérateur simulé n'ont jamais été livrés.
            Personne n'a payé et personne n'attend. Les relancer paierait une rédaction pour un
            rapport que personne ne lira : classez-les plutôt. La transaction reste, datée ; son
            profil est effacé et elle sort du compteur.
          </p>
          <ul className="listy">
            {testsEnEchec.map((t) => (
              <li key={t.txId}>
                <span>
                  {date(t.cree)} · {t.plan === "entreprise" ? "entreprise" : "salarié"} ·{" "}
                  {t.motif ?? "motif non renseigné"}{" "}
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={enCours === `classer_essai:${t.txId}`}
                    onClick={() =>
                      void agir(
                        "classer_essai",
                        t.txId,
                        "Classer cet achat de test ?\n\nPersonne ne l'a payé. Il sort du compteur, son profil est effacé et il ne pourra plus être relancé. Aucune rédaction, aucune dépense.",
                      )
                    }
                  >
                    Classer l'essai
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
        Ce qui concerne quelqu'un qui a donné de l'argent passe avant tout le
        reste — avant les tendances, avant les taux, avant la recette.
      */}
      {(attendent.length > 0 || aRembourser.length > 0 || echus.length > 0) && (
        <div data-groupe="agir" className="block pil-alertes">
          <h2>À traiter maintenant</h2>
          {attendent.length > 0 && (
            <p className="pil-alerte pil-rouge">
              {attendent.length} acheteur(s) ont payé et n'ont pas reçu leur rapport.
              {etat.rapportsDusMotifs && Object.keys(etat.rapportsDusMotifs).length > 0
                ? ` Motifs : ${Object.entries(etat.rapportsDusMotifs)
                    .map(([m, n]) => `${m} (${n})`)
                    .join(", ")}.`
                : ""}
            </p>
          )}
          {attendent.some((t) => t.rediGeable) && (
            <p>
              {/*
                Une panne commune bloque plusieurs acheteurs d'un coup — le
                compte du fournisseur à sec en est l'exemple. Les relancer un
                par un est une corvée qui invite à en oublier un.
              */}
              <button
                type="button"
                className="btn btn-ghost"
                disabled={enCours === "relancer_tous:"}
                onClick={() =>
                  void agir(
                    "relancer_tous",
                    "",
                    "Remettre en file toutes les transactions encore rédigeables ? Celles dont le diagnostic a disparu ne bougeront pas.",
                  )
                }
              >
                Tout relancer
              </button>
            </p>
          )}

          {aRembourser.length > 0 && (
            <p className="pil-alerte pil-rouge">
              {aRembourser.length} remboursement(s) demandé(s) et non encore rendu(s). La promesse
              court sur quatorze jours.
            </p>
          )}
          {echus.length > 0 && (
            <p className="pil-alerte pil-rouge">
              {echus.length} accompagnement(s) ont dépassé leurs quatre-vingt-dix jours sans être
              marqués honorés.
            </p>
          )}
        </div>
      )}

      {/*
        Ce que le tableau de bord ne dit pas : les durées, les motifs de la
        semaine, la dépense du jour.

        Le tableau de bord répond à « comment va le produit ». Ces trois
        blocs-là répondent à « qu'est-ce qui se passe en ce moment », qui est la
        question qu'on se pose quand on ouvre cette page.
      */}
      <div data-groupe="sante" className="block">
        <h2>La rédaction, mesurée</h2>
        {etat.durees && etat.durees.mesurees > 0 ? (
          <>
            <div className="pil-chiffres">
              <div className="pil-chiffre">
                <div className="pil-chiffre-lib">Médiane</div>
                <div className="pil-chiffre-val">{etat.durees.medianeMinutes} min</div>
                <div className="pil-chiffre-ref">sur {etat.durees.mesurees} rapports livrés</div>
              </div>
              <div className="pil-chiffre">
                <div className="pil-chiffre-lib">Neuf sur dix sous</div>
                <div className="pil-chiffre-val">{etat.durees.p90Minutes} min</div>
                <div className="pil-chiffre-ref">le client le plus mal loti sur dix</div>
              </div>
              <div className="pil-chiffre">
                <div className="pil-chiffre-lib">Pire cas</div>
                <div className="pil-chiffre-val">{etat.durees.pireMinutes} min</div>
                <div className="pil-chiffre-ref">
                  {(etat.durees.pireMinutes ?? 0) >= 9
                    ? "au-delà de neuf minutes, le travail planifié abandonne et reprend"
                    : "dans le budget de neuf minutes"}
                </div>
              </div>
              <div className="pil-chiffre">
                <div className="pil-chiffre-lib">Servis par le cache</div>
                <div className="pil-chiffre-val">
                  {etat.durees.partCache === null ? "—" : `${etat.durees.partCache} %`}
                </div>
                <div className="pil-chiffre-ref">ceux-là sortent en secondes</div>
              </div>
            </div>
            <p className="fine">
              Mesuré de la prise en charge à la livraison — l'attente en file n'y est pas, elle
              dépend du rythme du travail planifié et non de la rédaction.
            </p>
          </>
        ) : (
          <p className="fine">
            Aucune livraison mesurée pour l'instant. Une livraison d'essai suffit à peupler ce
            bloc.
          </p>
        )}
      </div>

      {etat.motifsSemaine && Object.keys(etat.motifsSemaine).length > 0 && (
        <div data-groupe="sante" className="block">
          <h2>Sur quoi les rédactions ont échoué, ces sept jours</h2>
          <p className="fine">
            Le tableau des achats montre l'instant présent. Celui-ci montre la semaine : un motif
            corrigé ce matin y figure encore, et c'est ce qui permet de savoir s'il a cessé.
          </p>
          <div style={{ overflowX: "auto" }}>
            <table className="pil-table">
              <thead>
                <tr>
                  <th>Motif</th>
                  <th>Échecs</th>
                  <th>Ce qu'il faut en faire</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(etat.motifsSemaine)
                  .sort(([, a], [, b]) => b - a)
                  .map(([motif, n]) => (
                    <tr key={motif}>
                      <td>
                        <code>{motif}</code>
                      </td>
                      <td style={{ fontVariantNumeric: "tabular-nums" }}>{n}</td>
                      <td className="fine">{MOTIFS[motif] ?? "motif non répertorié"}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {etat.depense && (
        <div data-groupe="sante" className="block">
          <h2>La dépense du jour</h2>
          <p className="fine">
            Au plafond, les appels modèle sont suspendus <strong>et</strong> la vente se ferme. Le
            savoir avant plutôt qu'après, c'est la différence entre relever un plafond et rembourser
            un acheteur.
          </p>
          <div className="pil-chiffres">
            <div className="pil-chiffre">
              <div className="pil-chiffre-lib">Dépensé</div>
              <div className="pil-chiffre-val">{fmt(etat.depense.depenseXof)} F</div>
              <div className="pil-chiffre-ref">{etat.depense.appels} appels au modèle</div>
            </div>
            <div className="pil-chiffre">
              <div className="pil-chiffre-lib">Plafond</div>
              <div className="pil-chiffre-val">
                {etat.depense.plafondXof > 0 ? `${fmt(etat.depense.plafondXof)} F` : "aucun"}
              </div>
              <div className="pil-chiffre-ref">
                {etat.depense.plafondXof > 0
                  ? `${etat.depense.partPourCent} % consommés`
                  : "un point d'entrée sans plafond est une facture ouverte"}
              </div>
            </div>
            <div className="pil-chiffre">
              <div className="pil-chiffre-lib">Reste</div>
              <div className="pil-chiffre-val">
                {etat.depense.resteXof === null ? "—" : `${fmt(etat.depense.resteXof)} F`}
              </div>
              <div className="pil-chiffre-ref">avant suspension des appels</div>
            </div>
          </div>
        </div>
      )}

      {etat.qualite && etat.qualite.mesures > 0 && (
        <div data-groupe="sante" className="block">
          <h2>La qualité des rapports partis</h2>
          <p className="fine">
            Deux barèmes d'une cinquantaine d'axes, notés sur cinq, calculés par le code sur chaque rapport
            livré — jamais par un second appel au modèle, qui coûterait un appel de plus et rendrait
            une note qu'on ne pourrait ni expliquer ni reproduire. Le premier lit ce qui est
            <strong> écrit</strong> ; le second juge le <strong>document</strong>, qui ne se connaît
            qu'une fois composé. Aucun des deux ne bloque une livraison : un acheteur qui a payé
            reçoit son document, même noté trois sur cinq.
          </p>
          <div className="pil-chiffres">
            <div className="pil-chiffre">
              <div className="pil-chiffre-lib">Contenu</div>
              <div className="pil-chiffre-val">{etat.qualite.moyenne} / 5</div>
              <div className="pil-chiffre-ref">sur {etat.qualite.mesures} rapports</div>
            </div>
            <div className="pil-chiffre">
              <div className="pil-chiffre-lib">Mise en page</div>
              <div className="pil-chiffre-val">
                {etat.qualite.moyenneDocument === null ? "—" : `${etat.qualite.moyenneDocument} / 5`}
              </div>
              <div className="pil-chiffre-ref">
                {etat.qualite.pages === null ? "pages non relevées" : `${etat.qualite.pages} pages en moyenne`}
              </div>
            </div>
            <div className="pil-chiffre">
              <div className="pil-chiffre-lib">La plus basse</div>
              <div className="pil-chiffre-val">{etat.qualite.pire} / 5</div>
              <div className="pil-chiffre-ref">ce que le plus mal loti a reçu</div>
            </div>
            {Object.entries(etat.qualite.familles).map(([nom, note]) => (
              <div className="pil-chiffre" key={nom}>
                <div className="pil-chiffre-lib">{nom}</div>
                <div className="pil-chiffre-val">{note} / 5</div>
                <div className="pil-chiffre-ref">{FAMILLES[nom] ?? ""}</div>
              </div>
            ))}
          </div>
          {etat.qualite.axesFaibles.length > 0 && (
            <>
              <p className="fine">
                Les axes le plus souvent sous trois. Ce ne sont pas des reproches au modèle : ce
                sont les consignes qui manquent au prompt, nommées plutôt que devinées.
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="pil-table">
                  <thead>
                    <tr>
                      <th>Axe</th>
                      <th>Rapports concernés</th>
                    </tr>
                  </thead>
                  <tbody>
                    {etat.qualite.axesFaibles.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <code>{a.id}</code>
                        </td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>{a.occurrences}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {etat.qualite.profils && etat.qualite.profils.length > 0 && (
            <>
              <p className="fine">
                Par profil. Mélangés, les trois se masquent l'un l'autre : un axe faible chez le seul
                dirigeant se dilue dans une moyenne que le salarié domine.
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="pil-table">
                  <thead>
                    <tr>
                      <th>Profil</th>
                      <th>Rapports</th>
                      <th>Contenu</th>
                      <th>Axes les plus faibles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {etat.qualite.profils.map((p) => (
                      <tr key={p.profil}>
                        <td>{PROFILS[p.profil]}</td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>{p.mesures}</td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>{p.moyenne} / 5</td>
                        <td>
                          {p.axesFaibles.length === 0
                            ? "aucun"
                            : p.axesFaibles.map((a, i) => (
                                <span key={a.id}>
                                  {i > 0 && ", "}
                                  <code>{a.id}</code> ({a.partPourCent} %)
                                </span>
                              ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {etat.qualite.versions && etat.qualite.versions.length > 0 && (
            <>
              <p className="fine">
                Par version, de la plus récente à la plus ancienne. C'est ici qu'une consigne ou un
                correctif se juge : si la note ne monte pas et que les retouches ne baissent pas, il
                n'a rien changé pour l'acheteur. Un « ? » désigne un rapport livré avant que la
                version ne soit notée.
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="pil-table">
                  <thead>
                    <tr>
                      <th>Profil</th>
                      <th>Consignes</th>
                      <th>Finition</th>
                      <th>Rapports</th>
                      <th>Contenu</th>
                      <th>Retouches par rapport</th>
                    </tr>
                  </thead>
                  <tbody>
                    {etat.qualite.versions.map((v) => (
                      <tr key={`${v.profil}|${v.consignes}|${v.finition}`}>
                        <td>{PROFILS[v.profil]}</td>
                        <td>
                          <code>{v.consignes}</code>
                        </td>
                        <td>
                          <code>{v.finition}</code>
                        </td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>{v.mesures}</td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>{v.moyenne} / 5</td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>
                          {v.retouchesParRapport === null ? "—" : v.retouchesParRapport}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {etat.qualite.retouches && etat.qualite.retouches.rediges > 0 && (
            <>
              <p className="fine">
                Ce que le code a dû reprendre sur les {etat.qualite.retouches.rediges} rapports
                rédigés — ceux servis par le cache ne comptent pas, ils ont été finis une fois. Une
                étape qui touche presque chaque rapport est une consigne que le prompt ne tient pas :
                c'est lui qu'il faut reprendre, plutôt qu'allonger la liste des réécritures.
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="pil-table">
                  <thead>
                    <tr>
                      <th>Étape</th>
                      <th>Rapports touchés</th>
                      <th>Chaînes reprises</th>
                    </tr>
                  </thead>
                  <tbody>
                    {etat.qualite.retouches.etapes.map((e) => (
                      <tr key={e.etape}>
                        <td>{ETAPES_FINITION[e.etape] ?? e.etape}</td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>
                          {e.rapportsTouches} ({e.partPourCent} %)
                        </td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>{e.chaines}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {etat.qualite.relecture && etat.qualite.relecture.relus > 0 && (
            <>
              <p className="fine">
                La relecture des {etat.qualite.relecture.relus} documents partis : le PDF lui-même,
                tel que l'acheteur l'ouvre, relu par le code à chaque livraison. C'est ici, et non
                en ouvrant les PDF un par un, que se voit un défaut de document. Un défaut qui
                touche plusieurs rapports se corrige ; un défaut isolé se surveille.
              </p>
              {etat.qualite.relecture.defauts.length === 0 ? (
                <p className="fine">Aucun défaut trouvé.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="pil-table">
                    <thead>
                      <tr>
                        <th>Défaut</th>
                        <th>Documents touchés</th>
                        <th>Occurrences</th>
                      </tr>
                    </thead>
                    <tbody>
                      {etat.qualite.relecture.defauts.map((d) => (
                        <tr key={d.id}>
                          <td>{DEFAUTS_RELECTURE[d.id] ?? d.id}</td>
                          <td style={{ fontVariantNumeric: "tabular-nums" }}>
                            {d.rapportsTouches} ({d.partPourCent} %)
                          </td>
                          <td style={{ fontVariantNumeric: "tabular-nums" }}>{d.occurrences}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {etat.metiers && etat.metiers.length > 0 && (
        <div data-groupe="mesure" className="block">
          <h2>Les métiers qui arrivent</h2>
          <p className="fine">
            Ce que les visiteurs saisissent, ramené à la forme canonique du modèle. Cette liste
            décide de trois choses : quelles pages métier écrire, quels packs réchauffer avant une
            campagne, et quels métiers faire entrer au référentiel. Le référentiel passe avant le
            cache : chaque ligne est un métier que le modèle sert faute de fiche relue, et les plus
            demandés sont les candidats — c'est le trafic qui les désigne, pas le jugé. Elle compte
            les lectures du cache — la toute première personne, celle qui paie la génération, n'y
            figure pas encore.
          </p>
          <div style={{ overflowX: "auto" }}>
            <table className="pil-table">
              <thead>
                <tr>
                  <th>Métier</th>
                  <th>Demandes servies</th>
                  <th>Référentiel</th>
                </tr>
              </thead>
              <tbody>
                {etat.metiers.map((m) => (
                  <tr key={m.metier}>
                    <td>{m.metier}</td>
                    <td style={{ fontVariantNumeric: "tabular-nums" }}>{m.vus}</td>
                    <td>{m.auReferentiel ? "déjà relu" : "candidat"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <EssaiCourriel session={session} actif={etat.courrielArme !== false} />

      {etat.modelesEssai && (
        <EssaiMesureBloc
          session={session}
          essais={etat.essaisMesures ?? []}
          modeles={etat.modelesEssai}
          production={etat.reglageProduction ?? null}
          onDemande={() => void charger()}
        />
      )}

      {/*
        Éprouver la livraison sans payer.

        La chaîne exercée est **exactement** celle d'un acheteur — même file,
        même bail, même générateur, même gabarit. Seul l'argent est absent.
        L'essai porte un identifiant de session de recette : il n'entre dans
        aucun compteur, ni ventes, ni recette, ni acheteurs en attente.
      */}
      {/*
        Les outils du moment — 25/08/2026.

        Le rapport ne nomme aucune marque, parce qu'elles changent plus vite que
        lui. C'est juste, et cela laissait le lecteur choisir seul : il lisait
        « un assistant conversationnel » et devait découvrir tout seul ce qui
        est gratuit depuis un téléphone.

        Cette page publie à côté, datée, ce que le document ne peut pas porter.
        C'est du contenu et non un réglage — d'où un bloc à part, et non une
        ligne de plus dans la liste des interrupteurs.
      */}
      <div data-groupe="reglages" className="block">
        <h2>Les outils du moment</h2>
        <p className="fine">
          Publiée sur <code>/outils</code>, et citée par chaque rapport. Une ligne par catégorie,
          trois champs séparés par une barre verticale&nbsp;: ce que dit le rapport | ce que c'est
          aujourd'hui | ce qu'il faut savoir. Les lignes vides et celles qui commencent par
          <code>#</code> sont ignorées.
        </p>
        <p className="fine">
          {etat.outils?.parDefaut
            ? `Aucune liste de votre main. La page sert la liste de départ écrite dans le code — ${etat.outils.lignes.length} ligne(s) — et dit à vos lecteurs qu'elle n'a pas encore été revue. Le champ ci-dessous la contient : amendez-la, publiez-la, et la mention disparaît.`
            : etat.outils?.majLe
              ? `Révisée le ${date(etat.outils.majLe)} — ${etat.outils.lignes.length} ligne(s). La page affiche cette date, et prévient au-delà de six mois.`
              : "Aucune liste publiée. La page dit alors qu'il n'y en a pas, et le rapport reste utilisable tel quel."}
        </p>
        <textarea
          id="outils-texte"
          aria-label="Les outils du moment, une ligne par catégorie"
          rows={8}
          defaultValue={
            etat.outils?.lignes.map((l) => `${l.categorie} | ${l.outils} | ${l.note}`).join("\n") ??
            ""
          }
          placeholder="Assistant conversationnel | Claude, ChatGPT, Gemini | versions gratuites suffisantes, utilisables depuis un téléphone"
          style={{ width: "100%", fontFamily: "var(--mono)", fontSize: "13px" }}
        />
        <p>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={enCours === "outils:"}
            onClick={() => {
              const champ = document.getElementById("outils-texte") as HTMLTextAreaElement | null;
              if (!champ) return;
              void agir("outils", "", "Publier cette liste sur /outils ?", false, undefined, {
                texte: champ.value,
              });
            }}
          >
            Publier la liste
          </button>
        </p>
      </div>

      <div data-groupe="reglages" className="block">
        <h2>Éprouver la livraison</h2>
        <p className="fine">
          Crée un achat fictif et le met en file. La rédaction part dans la minute et prend
          jusqu'à neuf minutes ; le lien apparaîtra dans le tableau ci-dessous. Aucun
          encaissement, et rien de tout cela n'entre dans vos chiffres.
        </p>
        {/*
          Deux spécimens, dits séparément.

          Une seule phrase pour les deux aurait laissé croire qu'un exemple
          publié couvre les deux produits. C'est précisément l'erreur qui a
          duré : le rapport d'entreprise se vendait 15 000 F derrière l'exemple
          d'un rapport à 3 000 F, et le tableau de bord affichait « un rapport
          d'exemple est publié ».
        */}
        <p className="fine">
          <b>Salarié — 3 000 F.</b>{" "}
          {etat.specimen?.publie
            ? `Publié depuis le ${date(etat.specimen.publieLe)}. Publiez-en un plus récent quand le gabarit change.`
            : "Aucun exemple publié. Les visiteurs n'ont rien à lire avant de payer — lancez un essai salarié, puis publiez-le."}
        </p>
        <p className="fine">
          <b>Entreprise — {fmt(Number(etat.reglages?.prixEntreprise ?? PLANS.entreprise))} F.</b>{" "}
          {etat.specimen?.entreprise?.publie
            ? `Publié depuis le ${date(etat.specimen.entreprise.publieLe)}.`
            : "Aucun exemple publié. C'est le produit le plus cher du site, et il se vend sans qu'on puisse en voir une page — lancez un essai dirigeant, puis publiez-le."}
        </p>
        <p>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={enCours?.startsWith("essai") === true || essaiEnFile("report") !== null}
            onClick={() => void agir("essai", "", undefined, false, "report")}
          >
            Rapport salarié
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={enCours?.startsWith("essai") === true || essaiEnFile("independant") !== null}
            onClick={() => void agir("essai", "", undefined, false, "independant")}
          >
            Rapport indépendant
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={enCours?.startsWith("essai") === true || essaiEnFile("entreprise") !== null}
            onClick={() => void agir("essai", "", undefined, false, "entreprise")}
          >
            Rapport entreprise
          </button>
        </p>
        {(["report", "independant", "entreprise"] as const).map((plan) => {
          const enFile = essaiEnFile(plan);
          if (enFile === null) return null;
          return (
            <p key={plan} className="fine" aria-live="polite">
              Un essai {plan === "report" ? "salarié" : plan === "independant" ? "indépendant" : "entreprise"} est en file depuis{" "}
              {date(enFile.cree)}&nbsp;: son lien apparaîtra dans « Les achats », ci-dessous, sous
              neuf minutes. Un second clic n'en lancerait pas un autre.
            </p>
          );
        })}
      </div>

      {/*
        Les interrupteurs du produit.

        Ils ne remplacent pas le manifeste de déploiement, qui décide de ce qui
        **existe** — quelle clé s'attache à quelle fonction. Ils décident de ce
        qui est **ouvert**, et cela se règle en exploitation, pas en poussant du
        code.

        Chaque bascule est consignée au journal ci-dessous : un interrupteur
        dont personne ne sait quand il a bougé est un interrupteur qu'on n'ose
        plus toucher.
      */}
      {/*
        La sentinelle, et surtout **par quelle voie** elle a parlé.

        Une adresse posée sans canal armé donne un écran qui affiche un
        destinataire, un exploitant qui se croit prévenu, et des alertes qui ne
        partent nulle part. L'écran doit donc dire ce qui est **réellement**
        parti, pas ce qui était configuré pour partir.
      */}
      {/*
        Les paiements qui ne sont jamais arrivés.

        Le produit ne peut pas dire **pourquoi** : Chariow ne notifie que les
        ventes réussies, et un paiement qui échoue ne revient jamais. Il peut
        dire **combien**, ce qui suffit à savoir s'il faut aller regarder chez
        eux — et c'est le seul chemin vers le motif.
      */}
      {etat.tunnels && (etat.tunnels.abandonnes > 0 || etat.tunnels.aboutis > 0) && (
        <div data-groupe="agir" className="block">
          <h2>Les paiements ouverts sans suite</h2>
          <p className="fine">
            Sur vingt-quatre heures : <b>{etat.tunnels.aboutis}</b> paiement
            {etat.tunnels.aboutis > 1 ? "s" : ""} abouti{etat.tunnels.aboutis > 1 ? "s" : ""},{" "}
            <b>{etat.tunnels.abandonnes}</b> tunnel{etat.tunnels.abandonnes > 1 ? "s" : ""} ouvert
            {etat.tunnels.abandonnes > 1 ? "s" : ""} depuis plus de trente minutes sans jamais
            aboutir.
          </p>
          <p className="fine">
            Le motif n'est pas lisible ici, et ne le sera jamais : Chariow ne notifie que les
            ventes réussies. Un paiement qui échoue ne revient pas — il laisse seulement un
            tunnel ouvert. <b>La cause vit dans leur tableau de bord.</b>
          </p>
        </div>
      )}

      {/*
        Ce que le limiteur de débit refuse — 31/08/2026.

        Ce panneau existe parce qu'une question est restée ouverte trois jours
        faute d'une console : la montée en charge du 31/08 avait envoyé
        vingt-six requêtes sans jamais recevoir de refus, et savoir si le
        limiteur comptait demandait de lire un document Firestore. La réponse
        tenait en trois nombres ; elle vit ici désormais.

        Le chiffre à surveiller n'est pas « des compteurs au plafond » — c'est
        le limiteur qui fait son travail. C'est **un compteur très au-dessus du
        plafond sur peu de compteurs** : la marque d'un partage d'adresse par un
        opérateur mobile, où des visiteurs légitimes se bloquent entre eux.
      */}
      {etat.pression && (
        <div data-groupe="sante" className="block">
          <h2>Ce que le limiteur de débit refuse</h2>
          {etat.pression.lignes.some((l) => l.compteurs > 0) ? (
            <>
              <div style={{ overflowX: "auto" }}>
                <table className="pil-table">
                  <thead>
                    <tr>
                      <th>Ce qui est protégé</th>
                      <th>Compteurs</th>
                      <th>Le plus haut</th>
                      <th>Plafond</th>
                      <th>Au plafond</th>
                    </tr>
                  </thead>
                  <tbody>
                    {etat.pression.lignes
                      .filter((l) => l.compteurs > 0)
                      .sort((a, b) => b.maxi / b.plafond - a.maxi / a.plafond)
                      .map((l) => (
                        <tr key={l.portee}>
                          <td>
                            {PORTEES[l.portee] ?? <code>{l.portee}</code>}
                          </td>
                          <td style={{ fontVariantNumeric: "tabular-nums" }}>{l.compteurs}</td>
                          <td style={{ fontVariantNumeric: "tabular-nums" }}>
                            <b>{l.maxi}</b>
                          </td>
                          <td style={{ fontVariantNumeric: "tabular-nums" }}>{l.plafond}</td>
                          <td style={{ fontVariantNumeric: "tabular-nums" }}>{l.bloquees}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <p className="fine">
                Sur les deux dernières heures. Un compteur au plafond n'est pas une panne : c'est
                la protection qui joue. Ce qui mérite un regard est un compteur <b>très</b>{" "}
                au-dessus du plafond alors qu'il y en a peu — le signe que plusieurs visiteurs
                partagent une adresse chez leur opérateur mobile et se bloquent les uns les
                autres.
              </p>
            </>
          ) : (
            <p className="fine">
              Aucun compteur sur les deux dernières heures, sur les{" "}
              {etat.pression.lignes.length} protections surveillées. Personne n'a été refusé, et
              personne n'en a approché.
            </p>
          )}
          <p className="fine">
            Aucune adresse n'apparaît ici, et n'y apparaîtra pas : elles sont des données
            personnelles, et ces trois nombres répondent sans qu'il faille savoir qui.
            {etat.pression.tronquee ? (
              <>
                {" "}
                <b>Lecture tronquée</b> — le nombre de compteurs est un plancher, pas un
                décompte.
              </>
            ) : null}
          </p>
        </div>
      )}

      <div data-groupe="sante" className="block">
        <h2>La sentinelle</h2>
        {etat.sentinelle?.le == null ? (
          <p className="fine">
            Elle n'a encore rien eu à dire. C'est le bon état : elle ne parle que quand quelque
            chose ne va pas, et se tait tant que rien ne change.
          </p>
        ) : (
          <>
            <p className="fine">
              Dernière annonce le <b>{date(etat.sentinelle.le)}</b>
              {etat.sentinelle.soucis.length > 0
                ? ` — ${etat.sentinelle.soucis.join(", ")}`
                : " — retour au calme"}
              .
            </p>
            <p className="fine">
              {etat.sentinelle.voie === "courriel" ? (
                <>
                  Partie <b>par courriel</b> : le canal est armé, vous recevez les alertes.
                </>
              ) : (
                <>
                  <b>Journal seulement</b> — rien ne vous est parvenu. Le canal de courriel n'est pas
                  armé : il manque la clé Brevo dans Secret Manager, l'expéditeur, ou{" "}
                  <code>courriel: true</code> dans le manifeste de déploiement. Les alertes vivent
                  dans Cloud Logging, où il faut aller les chercher.
                </>
              )}
            </p>
          </>
        )}
      </div>

      {/*
        Les mentions légales, et le verrou qu'elles commandent.

        Placées **avant** les interrupteurs, et c'est délibéré : parmi ces
        interrupteurs se trouve celui qui ouvre la caisse au public, et le
        serveur le refuse tant qu'une mention manque. Lire d'abord ce qui
        bloque, puis rencontrer le bouton bloqué, est l'ordre qui fait
        comprendre. L'inverse fait croire à une panne.
      */}
      <div data-groupe="reglages" className="block">
        <h2>Les mentions légales</h2>
        {(() => {
          const requises = MENTIONS_PILOTAGE.filter((m) => m.requise);
          const manquantes = requises.filter(
            (m) => typeof etat.reglagesPoses?.[m.cle] !== "string",
          ).length;
          return manquantes === 0 ? (
            <p className="fine">
              <b>Les deux mentions requises sont posées.</b> La vente peut être ouverte au
              public. Les facultatives s'affichent si vous les posez ; région d'hébergement,
              fournisseur du modèle, délai de remboursement et juridiction sont publiés par
              le serveur lui-même.
            </p>
          ) : (
            <p className="fine">
              <b>
                {manquantes} mention{manquantes > 1 ? "s" : ""} requise{manquantes > 1 ? "s" : ""}{" "}
                sur {requises.length} {manquantes > 1 ? "manquent" : "manque"}.
              </b>{" "}
              Tant qu'il en manque une, le serveur refuse d'ouvrir la vente au public : un
              acheteur doit pouvoir savoir à qui il a affaire et comment se retourner. Le
              diagnostic gratuit, lui, n'est pas concerné. Les facultatives et les dérivées
              du déploiement ne bloquent rien.
            </p>
          );
        })()}
        {MENTIONS_PILOTAGE.map((mention) => {
          const posee = etat.reglagesPoses?.[mention.cle];
          const valeur = typeof posee === "string" ? posee : "";
          return (
            <p key={mention.cle}>
              <label htmlFor={`reglage-${mention.cle}`}>
                <b>{mention.titre}</b>
              </label>
              {/*
                `onBlur`, comme l'adresse de contact et pour la même raison :
                poser à chaque frappe consignerait trente refus au journal
                avant la bonne valeur, et rendrait illisible le seul registre
                qui dit qui a changé quoi.
              */}
              <input
                id={`reglage-${mention.cle}`}
                type="text"
                autoComplete="off"
                maxLength={200}
                defaultValue={valeur}
                placeholder={mention.exemple}
                disabled={enCours === `reglage:${mention.cle}`}
                style={{ maxWidth: "340px" }}
                onBlur={(evenement) => {
                  const pose = evenement.currentTarget.value.trim();
                  if (pose === "" || pose === valeur) return;
                  void agir("reglage", mention.cle, undefined, false, undefined, {
                    cle: mention.cle,
                    valeur: pose,
                  });
                }}
              />
              <span className="fine" style={{ display: "block", marginTop: "6px" }}>
                <b>{valeur || "— en attente"}</b> — {mention.ou}
              </span>
            </p>
          );
        })}
      </div>

      <div data-groupe="reglages" className="block">
        <h2>Les réglages</h2>
        <p className="fine">
          Effet immédiat côté base, et jusqu'à une minute pour que toutes les instances le voient.
          Chaque changement est consigné.
        </p>
        {REGLAGES.map((reglage) => {
          const effectif = etat.reglages?.[reglage.cle];
          /*
            « Décidé » et « par défaut » ne se disent pas pareil.

            Un interrupteur ouvert parce que personne n'y a touché et un
            interrupteur ouvert parce que quelqu'un l'a ouvert produisent le
            même comportement, mais pas la même confiance — et pour la formule
            à 9 000 F, le défaut vient d'une variable de déploiement qu'on ne
            voit pas depuis cet écran. L'écran le dit donc.
          */
          const decide = etat.reglagesPoses?.[reglage.cle] !== undefined;

          if (reglage.nombre) {
            const valeur = typeof effectif === "number" ? effectif : 0;
            return (
              <p key={reglage.cle}>
                <label htmlFor={`reglage-${reglage.cle}`}>
                  <b>{reglage.titre}</b>
                </label>
                <input
                  id={`reglage-${reglage.cle}`}
                  type="number"
                  min={0}
                  step={1000}
                  inputMode="numeric"
                  defaultValue={valeur}
                  disabled={enCours === `reglage:${reglage.cle}`}
                  style={{ maxWidth: "220px" }}
                  onBlur={(evenement) => {
                    const pose = Number(evenement.currentTarget.value);
                    if (!Number.isFinite(pose) || pose < 0 || pose === valeur) return;
                    void agir("reglage", reglage.cle, undefined, false, undefined, {
                      cle: reglage.cle,
                      valeur: pose,
                    });
                  }}
                />
                <span className="fine" style={{ display: "block", marginTop: "6px" }}>
                  <b>
                    {valeur > 0
                      ? `${valeur.toLocaleString("fr-FR")} ${reglage.nombre.unite}`
                      : reglage.nombre.zero}
                  </b>
                  {decide ? " — posé ici" : " — valeur du déploiement"} — {reglage.explication}
                </span>
              </p>
            );
          }

          if (reglage.texte) {
            const valeur = typeof effectif === "string" ? effectif : "";
            const choix = reglage.texte.choix;
            return (
              <p key={reglage.cle}>
                <label htmlFor={`reglage-${reglage.cle}`}>
                  <b>{reglage.titre}</b>
                </label>
                {choix ? (
                  <select
                    id={`reglage-${reglage.cle}`}
                    value={valeur}
                    disabled={enCours === `reglage:${reglage.cle}`}
                    style={{ maxWidth: "260px" }}
                    onChange={(evenement) => {
                      const pose = evenement.currentTarget.value;
                      if (pose === valeur) return;
                      void agir("reglage", reglage.cle, undefined, false, undefined, {
                        cle: reglage.cle,
                        valeur: pose,
                      });
                    }}
                  >
                    {choix.map((option) => (
                      <option key={option.valeur} value={option.valeur}>
                        {option.libelle}
                      </option>
                    ))}
                  </select>
                ) : (
                  /*
                    `onBlur` et non `onChange` : une adresse se tape lettre par
                    lettre, et poser à chaque frappe enverrait une trentaine de
                    réglages refusés avant le bon — chacun consigné dans le
                    journal, qui deviendrait illisible là où il doit dire qui a
                    changé quoi.
                  */
                  <input
                    id={`reglage-${reglage.cle}`}
                    type="email"
                    inputMode="email"
                    autoComplete="off"
                    defaultValue={valeur}
                    placeholder={reglage.texte.exemple}
                    disabled={enCours === `reglage:${reglage.cle}`}
                    style={{ maxWidth: "280px" }}
                    onBlur={(evenement) => {
                      const pose = evenement.currentTarget.value.trim();
                      if (pose === "" || pose === valeur) return;
                      void agir("reglage", reglage.cle, undefined, false, undefined, {
                        cle: reglage.cle,
                        valeur: pose,
                      });
                    }}
                  />
                )}
                <span className="fine" style={{ display: "block", marginTop: "6px" }}>
                  <b>{valeur || "—"}</b>
                  {decide ? " — posé ici" : " — valeur par défaut"} — {reglage.explication}
                </span>
              </p>
            );
          }

          const ouvert = effectif !== false;
          return (
            <p key={reglage.cle}>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={enCours === `reglage:${reglage.cle}`}
                aria-pressed={ouvert}
                onClick={() =>
                  void agir("reglage", reglage.cle, undefined, false, undefined, {
                    cle: reglage.cle,
                    valeur: !ouvert,
                  })
                }
              >
                {ouvert ? `Fermer — ${reglage.titre}` : `Ouvrir — ${reglage.titre}`}
              </button>
              <span className="fine" style={{ display: "block", marginTop: "6px" }}>
                <b>{ouvert ? "Ouvert" : "Fermé"}</b>
                {decide ? " — posé ici" : " — par défaut"} — {reglage.explication}
              </span>
            </p>
          );
        })}
      </div>

      <div data-groupe="agir" className="block">
        <h2>Les achats</h2>
        <p className="fine">
          Ni numéro ni contenu de diagnostic : le serveur ne les envoie pas. « Rédigeable » dit si
          les données nécessaires au rapport sont encore là — sans elles, aucune reprise n'aboutit.
        </p>
        {/* L'annonce vocale de ce qui bouge : la table, elle, se met à jour en silence. */}
        <p className="fine" aria-live="polite">
          {enVol
            ? "Une livraison est en cours : cette page se met à jour toutes les quinze secondes."
            : "Aucune livraison en cours."}
        </p>
        {visibles.length === 0 ? (
          <p className="fine">Aucun achat pour l'instant.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="pil-table">
              <thead>
                <tr>
                  <th>Achat</th>
                  <th>Formule</th>
                  <th>Montant</th>
                  <th>État</th>
                  <th>Depuis</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((t) => (
                  <tr key={t.txId}>
                    <td>
                      <code>{court(t.txId)}</code>
                      <br />
                      <span className="fine">{t.essai ? "essai — sans encaissement" : t.operateur}</span>
                    </td>
                    <td>{t.plan}</td>
                    <td style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(t.montant)} F</td>
                    <td>
                      {STATUTS[t.statut] ?? t.statut}
                      {progression(t) !== null && (
                        <>
                          <br />
                          <span className="fine">{progression(t)}</span>
                        </>
                      )}
                      {t.motif !== null && (
                        <>
                          <br />
                          <span className="fine">{MOTIFS[t.motif] ?? t.motif}</span>
                        </>
                      )}
                      {/* Le texte de l'erreur, pour les essais seuls : « autre »
                          ne dit pas quoi corriger, lui si. */}
                      {t.note !== null && (
                        <>
                          <br />
                          <code className="fine" style={{ wordBreak: "break-word" }}>
                            {t.note}
                          </code>
                        </>
                      )}
                      {t.rembourseLe !== null && (
                        <>
                          <br />
                          <span className="fine">remboursé le {date(t.rembourseLe)}</span>
                        </>
                      )}
                      {t.url !== null && (
                        <>
                          <br />
                          <a href={t.url} target="_blank" rel="noopener noreferrer">
                            Ouvrir le rapport
                          </a>
                        </>
                      )}
                      {/*
                        Le nombre de remises, et le seuil au-delà duquel il se
                        remarque.

                        L'adresse de remise porte son jeton : elle fonctionne
                        pour quiconque la reçoit. Deux ou trois remises sont
                        normales — le téléphone, l'ordinateur, une relecture
                        trois semaines plus tard. Une douzaine ne l'est plus, et
                        c'est la seule chose que ce compteur prétend dire.

                        Aucun blocage : la décision revient à qui lit cette
                        ligne.
                      */}
                      {typeof t.remises === "number" && t.remises > 0 && (
                        <div className="fine" style={{ marginTop: "4px" }}>
                          {t.remises} remise{t.remises > 1 ? "s" : ""}
                          {t.remises >= SEUIL_REMISES ? " — inhabituel" : ""}
                          {t.derniereRemise ? `, dernière le ${date(t.derniereRemise)}` : ""}
                        </div>
                      )}
                    </td>
                    <td className="fine">{date(t.maj)}</td>
                    <td>
                      {t.statut === "echec_livraison" && t.rediGeable && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={enCours === `relancer:${t.txId}`}
                          onClick={() => void agir("relancer", t.txId)}
                        >
                          Relancer
                        </button>
                      )}
                      {/*
                        Publier un essai comme spécimen public.

                        C'est la seule preuve honnête qu'on puisse montrer avant
                        l'achat : le document lui-même, produit par la même
                        chaîne, sur un profil fictif qui n'appartient à personne.
                        Le rapport d'un acheteur, lui, ne se publie jamais.
                      */}
                      {t.essai && t.statut === "livre" && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={enCours === `specimen:${t.txId}`}
                          onClick={() =>
                            void agir(
                              "specimen",
                              t.txId,
                              t.plan === "entreprise"
                                ? "Publier ce rapport d'entreprise comme exemple public, visible de tous avant l'achat ?"
                                : "Publier ce rapport comme exemple public, visible de tous avant l'achat ?",
                            )
                          }
                        >
                          Publier comme spécimen
                        </button>
                      )}
                      {/*
                        Fermer une adresse qui circule, et la rouvrir.

                        Le geste est réversible — « Rouvrir l'adresse » réécrit
                        un jeton neuf sur le fichier déjà rangé — et c'est ce
                        qui permet de couper sans crainte. Le fichier reste
                        rangé, la transaction reste payée, le remboursement
                        reste dû si on le demande : seule l'adresse cesse de
                        répondre.

                        La condition portait sur `url`, que le back-office ne
                        renseigne que pour les essais : le bouton n'apparaissait
                        jamais sur la transaction d'un vrai acheteur, c'est-à-
                        dire dans le seul cas où il sert. `adresseOuverte` dit
                        la même chose sans livrer le lien de qui que ce soit.
                      */}
                      {t.statut === "livre" && t.adresseOuverte && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={enCours === `revoquer:${t.txId}`}
                          onClick={() =>
                            void agir(
                              "revoquer",
                              t.txId,
                              "Fermer l'adresse de remise de ce rapport ?\n\nLe fichier et la transaction restent intacts. L'acheteur perdra l'accès à son lien — « Rouvrir l'adresse » lui en rendra un neuf, renvoyé par courriel s'il en a laissé un.",
                            )
                          }
                        >
                          Fermer l'adresse
                        </button>
                      )}
                      {/*
                        `=== false` et non `!` : un serveur qui ne renseigne pas
                        encore ce champ ne doit pas faire apparaître « Rouvrir »
                        sur toutes les transactions livrées.
                      */}
                      {t.statut === "livre" && t.adresseOuverte === false && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={enCours === `rouvrir:${t.txId}`}
                          onClick={() =>
                            void agir(
                              "rouvrir",
                              t.txId,
                              "Rouvrir l'adresse de remise de ce rapport ?\n\nUn lien neuf remplace celui qui avait été fermé — l'ancien reste mort. Il est renvoyé par courriel si l'acheteur en a laissé un, et reste retrouvable avec sa référence. Le journal dira lequel des deux chemins a servi. Aucune rédaction, aucune dépense : le document est déjà là.",
                            )
                          }
                        >
                          Rouvrir l'adresse
                        </button>
                      )}
                      {!t.essai &&
                        (t.statut === "echec_livraison" ||
                          t.statut === "livre" ||
                          t.statut === "paye" ||
                          // Encaissé au mauvais montant : le débit est réel, donc
                          // le remboursement se consigne comme pour les autres.
                          t.statut === "montant_incoherent") &&
                        t.rembourseLe === null && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            disabled={enCours === `rembourse:${t.txId}`}
                            onClick={() =>
                              void agir(
                                "rembourse",
                                t.txId,
                                t.statut === "livre"
                                  ? `Confirmer que les ${fmt(t.montant)} F ont été rendus chez l'opérateur ?\n\nCe rapport a été LIVRÉ : le marquer remboursé retirera à l'acheteur l'accès à son document.\n\nCette page consigne le geste, elle ne déplace pas l'argent.`
                                  : `Confirmer que les ${fmt(t.montant)} F ont été rendus chez l'opérateur ? Cette page consigne le geste, elle ne déplace pas l'argent.`,
                                // Le serveur refuse de retirer l'accès sans cet
                                // aveu explicite : la fenêtre ci-dessus vient
                                // de le dire, le drapeau le prouve.
                                t.statut === "livre",
                              )
                            }
                          >
                            Marquer remboursé
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {etat.transactions.length > 12 && (
          <p>
            <button type="button" className="btn btn-ghost" onClick={() => setTout(!tout)}>
              {tout ? "N'afficher que les douze derniers" : `Afficher les ${etat.transactions.length}`}
            </button>
          </p>
        )}
      </div>

      {etat.engagements.length > 0 && (
        <div data-groupe="agir" className="block">
          <h2>Les accompagnements dus</h2>
          <p className="fine">
            Chaque formule vendue engage quatre-vingt-dix jours. Cette page consigne la dette ; elle
            ne la rend pas.
          </p>
          <div style={{ overflowX: "auto" }}>
            <table className="pil-table">
              <thead>
                <tr>
                  <th>Achat</th>
                  <th>Début</th>
                  <th>Échéance</th>
                  <th>État</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {etat.engagements.map((e) => (
                  <tr key={e.txId}>
                    <td>
                      <code>{court(e.txId)}</code>
                    </td>
                    <td className="fine">{date(e.debut)}</td>
                    <td className="fine">{date(e.fin)}</td>
                    <td>
                      {e.honoreLe !== null
                        ? `honoré le ${date(e.honoreLe)}`
                        : e.echu
                          ? "échu, non honoré"
                          : "en cours"}
                    </td>
                    <td>
                      {e.honoreLe === null && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={enCours === `engagement_honore:${e.txId}`}
                          onClick={() =>
                            void agir(
                              "engagement_honore",
                              e.txId,
                              "Confirmer que l'accompagnement a bien été rendu ?",
                            )
                          }
                        >
                          Marquer honoré
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {etat.journal.length > 0 && (
        <div data-groupe="reglages" className="block">
          <h2>Ce qui a été fait</h2>
          <p className="fine">
            Chaque geste posé depuis cette page est daté. Une opération qui ne se prouve pas ne vaut
            rien le jour où quelqu'un la conteste.
          </p>
          <ul className="pil-journal">
            {etat.journal.map((entree, i) => (
              <li key={`${entree.le}-${i}`}>
                <span className="fine">{date(entree.le)}</span> — {entree.action} sur{" "}
                <code>{court(entree.txId)}</code>
                {entree.note !== null && <span className="fine"> ({entree.note})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Éprouver la remise par courriel — 25/08/2026.
 *
 * ## Le seul échec que le code ne peut pas voir
 *
 * Le canal a trois façons de tomber. Deux se lisent dans les journaux : la clé
 * manque, ou le fournisseur refuse. La troisième ne s'y lit pas — **le message
 * part, il est accepté, et il atterrit dans les indésirables**. De notre côté
 * tout est vert ; l'acheteur, lui, n'a rien reçu.
 *
 * C'est de loin le plus probable pour un domaine jeune, et cela ne se mesure
 * que d'une façon : s'écrire à soi-même, chez plusieurs fournisseurs, et aller
 * regarder où le message est tombé. D'où ce bloc.
 *
 * ## Pourquoi il a son propre appel réseau
 *
 * `agir` recharge le tableau et ne lit pas le corps de la réponse. Or ici, le
 * corps **est** le résultat : le serveur rend `courriel_inactif` quand la clé
 * ou l'expéditeur manquent, `courriel_refuse_401` quand la clé est mauvaise,
 * `courriel_refuse_400` quand l'expéditeur n'est pas validé chez le
 * fournisseur. Trois pannes, trois gestes différents — les confondre dans un
 * « l'action n'a pas abouti » ferait perdre l'après-midi.
 */
function EssaiCourriel({ session, actif }: { session: string; actif: boolean }) {
  const [adresse, setAdresse] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [issue, setIssue] = useState<string | null>(null);

  /*
    La variante est un argument, pas un état.

    Écrite en état, elle aurait été posée par `setPlan` juste avant l'appel — et
    lue par l'envoi **avant** que React ait rendu la mise à jour. Le premier clic
    aurait donc envoyé la version salarié quel que soit le bouton pressé, et le
    second aurait envoyé la bonne : le genre de défaut qui passe une relecture
    parce qu'il finit par marcher.
  */
  const envoyer = async (plan: "report" | "entreprise") => {
    setEnCours(true);
    setIssue(null);
    try {
      const r = await fetch(`${CONFIG.pilotageEndpoint}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-pilotage": session },
        body: JSON.stringify({ action: "courriel_test", txId: "", destinataire: adresse, plan }),
      });
      const reponse = (await r.json()) as { fait?: boolean; motif?: string; erreur?: string };
      if (r.status === 429) {
        setIssue("Trop d'envois sur la dernière heure. Le contrôle est borné à huit — au-delà, ce n'est plus un contrôle.");
        return;
      }
      if (reponse.erreur === "adresse_invalide") {
        setIssue("Cette adresse n'en est pas une. Vérifiez la saisie.");
        return;
      }
      if (!r.ok) {
        setIssue(`L'envoi n'a pas abouti (${reponse.erreur ?? r.status}).`);
        return;
      }
      if (reponse.fait) {
        setIssue(
          `Parti vers ${adresse}. Allez le lire — et regardez d'abord dans les indésirables : c'est là que finit un domaine jeune, et c'est précisément ce que ce bouton sert à découvrir.`,
        );
        return;
      }
      setIssue(motifLisible(reponse.motif ?? ""));
    } catch {
      setIssue("L'envoi n'a pas abouti.");
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div data-groupe="reglages" className="block">
      <h2>Éprouver la remise par courriel</h2>
      <p className="fine">
        Envoie le <strong>vrai message de remise</strong> — même sujet, même corps, même
        expéditeur — à l'adresse de votre choix. Le lien mène au rapport d'exemple public et la
        référence est fictive : aucun rapport payé ne part jamais par ce bouton.
      </p>
      {!actif && (
        <p className="fine">
          Le canal n'est pas armé : il manque la clé dans Secret Manager ou l'adresse
          d'expédition. L'envoi vous dira lequel des deux.
        </p>
      )}
      <p className="fine">
        Faites-le vers au moins deux fournisseurs différents — une adresse Gmail et une adresse
        Outlook, par exemple. Ce qu'on cherche à savoir n'est pas « le message part-il ? », que
        le journal dit déjà, mais « où atterrit-il ? », que personne ne peut dire à votre place.
      </p>
      <p className="fine">
        Deux messages seulement, pour trois rapports : l'indépendant reçoit le même message de
        remise que le salarié — c'est le document qui change, pas le courriel. Pour relire les
        trois documents, lancez les trois livraisons d'essai plus haut.
      </p>
      <div className="field">
        <label htmlFor="courriel-essai">Adresse de destination</label>
        <input
          type="email"
          id="courriel-essai"
          autoComplete="off"
          placeholder="vous@exemple.com"
          value={adresse}
          onChange={(e) => setAdresse(e.target.value)}
        />
      </div>
      <p>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={enCours || adresse.trim() === ""}
          onClick={() => void envoyer("report")}
        >
          {enCours ? "Envoi…" : "Envoyer la version salarié et indépendant"}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={enCours || adresse.trim() === ""}
          onClick={() => void envoyer("entreprise")}
        >
          Envoyer la version dirigeant
        </button>
      </p>
      {issue !== null && (
        <p className="fine" aria-live="polite">
          {issue}
        </p>
      )}
    </div>
  );
}

/**
 * Le mot que porte chaque voyant.
 *
 * La couleur ne dit rien toute seule : un écran lu en plein soleil, un daltonien,
 * une capture d'écran en noir et blanc dans une conversation — trois façons
 * courantes de perdre l'information. Le mot, lui, survit aux trois.
 */
const MOTS: Record<Condition["feu"], string> = {
  vert: "ok",
  orange: "à voir",
  rouge: "panne",
  inconnu: "?",
};

/**
 * La tour de contrôle — 25/08/2026.
 *
 * Le tableau de bord mesurait l'activité du produit ; il ne disait pas son état
 * de vol. « La boutique encaisse-t-elle », « le stockage répond-il aux
 * fonctions », « les deux exemples sont-ils publiés » : chacune de ces questions
 * avait sa réponse quelque part, aucune n'était à l'écran, et le 25/08 elles se
 * sont posées une par une, à la voix.
 *
 * Le verdict est calculé côté serveur (`etatDeVol.ts`) : ce composant l'affiche
 * et n'en décide rien. Une règle métier qui vit dans du JSX ne se teste pas.
 */
function TourDeControle({ vol }: { vol: { peutVendre: boolean | null; conditions: Condition[] } }) {
  const bloquantes = vol.conditions.filter((c) => c.bloquant && c.feu !== "vert");
  return (
    <div data-groupe="agir sante" className="block pil-tour">
      <h2>Peut-on vendre, maintenant&nbsp;?</h2>
      {/*
        Trois réponses et non deux. « On ne sait pas » n'est pas « non » : le
        25/08, un repli pris pour une mesure a fait chercher une panne de
        stockage une journée entière. Une tour de contrôle qui invente un
        verdict est pire qu'une tour muette.
      */}
      <p className={`pil-tour-verdict pil-tour-${vol.peutVendre === null ? "inconnu" : vol.peutVendre ? "vert" : "rouge"}`}>
        {vol.peutVendre === null
          ? "On ne peut pas le dire : une condition n'a pas pu être vérifiée."
          : vol.peutVendre
            ? "Oui. Un achat peut aboutir de bout en bout : encaissement, rédaction, remise."
            : `Non. ${bloquantes.length === 1 ? "Une condition bloque" : `${bloquantes.length} conditions bloquent`} la chaîne.`}
      </p>
      <ul className="pil-tour-liste">
        {vol.conditions.map((c) => (
          <li key={c.cle} className={`pil-tour-ligne pil-tour-${c.feu}`}>
            <span className="pil-tour-feu">
              {MOTS[c.feu]}
              {c.bloquant && c.feu !== "vert" ? (
                <span className="pil-tour-bloc"> · bloque</span>
              ) : null}
            </span>
            <span className="pil-tour-dit">
              <b>{c.titre}.</b> {c.dit}
              {c.geste !== undefined && <em className="pil-tour-geste">{c.geste}</em>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Traduire le motif du fournisseur en geste à faire.
 *
 * Un code d'erreur recopié tel quel oblige l'exploitant à aller le chercher
 * ailleurs. Ces trois-là couvrent tout ce qui arrive en pratique, et chacun
 * appelle une action différente.
 */
function motifLisible(motif: string): string {
  if (motif.includes("courriel_inactif")) {
    return "Le canal n'est pas armé : la clé COURRIEL_CLE manque dans Secret Manager, ou la variable COURRIEL_EXPEDITEUR n'est pas posée. Les deux sont nécessaires.";
  }
  if (motif.includes("_401") || motif.includes("_403")) {
    return "Le fournisseur a refusé la clé. Elle est absente, révoquée, ou ce n'est pas une clé d'API transactionnelle.";
  }
  if (motif.includes("_400")) {
    return "Le fournisseur a refusé la demande — le plus souvent parce que l'adresse d'expédition n'est pas encore validée chez lui. Validez le domaine dans votre compte, puis réessayez.";
  }
  return `L'envoi a échoué : ${motif}`;
}

const PROFILS_ESSAI: Record<MesureEssai["cas"], string> = {
  salarie: "Salarié",
  independant: "À son compte",
  dirigeant: "Dirigeant",
};

/**
 * L'essai mesuré d'un réglage de rédaction — 02/10/2026.
 *
 * Le 29/09, Opus 5.5 est passé en production sans avoir rédigé un rapport
 * ailleurs, et la première livraison a été coupée (#242, #243). Ce bloc fait
 * rédiger les trois rapports d'essai par la fonction déployée — avec la clé de
 * production, là où elle est déjà — sous le réglage choisi, sans cache et sans
 * rien envoyer, puis montre si chacun aurait été livré à temps.
 */
function EssaiMesureBloc({
  session,
  essais,
  modeles,
  production,
  onDemande,
}: {
  session: string;
  essais: EssaiMesure[];
  modeles: string[];
  production: { modele: string; effort: string } | null;
  onDemande: () => void;
}) {
  const [modele, setModele] = useState(modeles[modeles.length - 1] ?? "");
  const [effort, setEffort] = useState("low");
  const [enCours, setEnCours] = useState(false);
  const [issue, setIssue] = useState<string | null>(null);
  const attend = essais.some((e) => !e.fini);

  const lancer = async () => {
    setEnCours(true);
    setIssue(null);
    try {
      const r = await fetch(`${CONFIG.pilotageEndpoint}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-pilotage": session },
        body: JSON.stringify({ action: "essai_mesure", txId: "", modele, effort }),
      });
      const reponse = (await r.json()) as { fait?: boolean; erreur?: string };
      if (r.status === 409) {
        setIssue("Un essai est déjà en cours. Il faut attendre qu'il finisse : deux essais à la fois doubleraient la dépense.");
        return;
      }
      if (!r.ok || !reponse.fait) {
        setIssue(`La demande n'a pas abouti (${reponse.erreur ?? r.status}).`);
        return;
      }
      setIssue("Demande enregistrée. Un rapport est rédigé par minute de passage, chacun en quelques minutes : comptez un quart d'heure à une demi-heure. Cette page se relit seule.");
      onDemande();
    } catch {
      setIssue("La demande n'a pas abouti.");
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div data-groupe="reglages" className="block">
      <h2>Mesurer un réglage de rédaction</h2>
      <p className="fine">
        Fait rédiger les trois rapports d'essai — salarié, à son compte, dirigeant — par la fonction
        déployée, avec la clé de production et le réglage choisi ci-dessous. Même prompt, même
        validation, même finition qu'un achat ; ni cache, ni envoi, ni transaction. La dépense est
        réelle et comptée au plafond du jour : quelques centaines de francs par essai.
        {production && (
          <>
            {" "}
            Réglage en production : <code>{production.modele}</code>, effort <code>{production.effort}</code>.
          </>
        )}
      </p>
      <div className="pil-chiffres">
        <label className="pil-chiffre">
          <span className="pil-chiffre-lib">Modèle</span>
          <select value={modele} onChange={(e) => setModele(e.target.value)} disabled={attend || enCours}>
            {modeles.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="pil-chiffre">
          <span className="pil-chiffre-lib">Effort</span>
          <select value={effort} onChange={(e) => setEffort(e.target.value)} disabled={attend || enCours}>
            {["low", "medium", "high"].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>
      <button type="button" className="btn" onClick={() => void lancer()} disabled={attend || enCours}>
        {attend ? "Essai en cours…" : "Lancer l'essai mesuré"}
      </button>
      {issue && (
        <p className="fine" role="status">
          {issue}
        </p>
      )}
      {essais.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table className="pil-table">
            <thead>
              <tr>
                <th>Réglage</th>
                <th>Profil</th>
                <th>Issue</th>
                <th>Durée</th>
                <th>Coût</th>
                <th>Note</th>
                <th>Retouches</th>
              </tr>
            </thead>
            <tbody>
              {essais.flatMap((e) =>
                e.cas.map((cas, i) => {
                  const m = e.mesures[i];
                  return (
                    <tr key={`${e.id}-${cas}`}>
                      <td>
                        {i === 0 && (
                          <>
                            <code>{e.modele}</code> · {e.effort}
                            <br />
                            <span className="fine">{new Date(e.demandeLe).toLocaleString("fr-FR")}</span>
                          </>
                        )}
                      </td>
                      <td>{PROFILS_ESSAI[cas]}</td>
                      <td>
                        {m
                          ? m.redige
                            ? m.tientDansLeBudget
                              ? "livrable"
                              : "hors budget de livraison"
                            : `échec : ${m.motif ?? "inconnu"}`
                          : e.fini
                            ? e.arret === "plafond_atteint"
                              ? "non rédigé : plafond du jour atteint"
                              : "non rédigé"
                            : "en attente"}
                      </td>
                      <td style={{ fontVariantNumeric: "tabular-nums" }}>
                        {m ? `${m.dureeS} s · ${m.tentatives} tent.` : "—"}
                      </td>
                      <td style={{ fontVariantNumeric: "tabular-nums" }}>{m ? `${m.coutXof} F` : "—"}</td>
                      <td style={{ fontVariantNumeric: "tabular-nums" }}>
                        {m?.note != null ? `${m.note} / 5` : "—"}
                      </td>
                      <td>
                        {m?.retouches
                          ? Object.entries(m.retouches)
                              .map(([k, v]) => `${k} ${v}`)
                              .join(", ")
                          : "—"}
                      </td>
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
